import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBFreelancerAttachment, DBFreelancerComment } from "@/lib/db-types";

/**
 * VIB-2925: kommentarlogg med svar, @-nevning og filer per frilanser.
 * Filer ligger i den private bucketen freelancer-files og åpnes via signerte
 * URL-er. Varsel ved @-nevning lages av DB-triggeren notif_freelancer_mention.
 */

export const FREELANCER_FILES_BUCKET = "freelancer-files";
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

export interface FreelancerCommentRow extends DBFreelancerComment {
  author: { full_name: string; avatar_url: string | null } | null;
  attachments: DBFreelancerAttachment[];
  replies: FreelancerCommentRow[];
}

export function useFreelancerComments(freelancerId: string | undefined) {
  return useQuery({
    queryKey: ["freelancer-comments", freelancerId],
    enabled: !!freelancerId,
    queryFn: async (): Promise<FreelancerCommentRow[]> => {
      const { data, error } = await supabase
        .from("freelancer_comments")
        .select("*, author:users!freelancer_comments_author_id_fkey(full_name, avatar_url), attachments:freelancer_comment_attachments(*)")
        .eq("freelancer_id", freelancerId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      const all = ((data ?? []) as unknown as FreelancerCommentRow[]).map((c) => ({ ...c, attachments: c.attachments ?? [], replies: [] }));
      const byId = new Map(all.map((c) => [c.id, c]));
      const top: FreelancerCommentRow[] = [];
      for (const c of all) {
        const parent = c.parent_comment_id ? byId.get(c.parent_comment_id) : undefined;
        if (parent) parent.replies.push(c);
        else top.push(c);
      }
      return top.reverse();
    },
  });
}

function safeFileName(name: string): string {
  return name.normalize("NFKD").replace(/[^\w.-]+/g, "_").slice(-120);
}

export function useCreateFreelancerComment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      freelancer_id: string;
      body: string;
      mentioned_user_ids: string[];
      parent_comment_id?: string | null;
      files?: File[];
    }) => {
      if (!user?.id) throw new Error("Ikke innlogget");
      const files = input.files ?? [];
      const tooBig = files.find((f) => f.size > MAX_FILE_BYTES);
      if (tooBig) throw new Error(`${tooBig.name} er større enn 25 MB`);
      if (!input.body.trim() && files.length === 0) throw new Error("Skriv noe eller legg ved en fil");

      const { data, error } = await supabase
        .from("freelancer_comments")
        .insert({
          freelancer_id: input.freelancer_id,
          author_id: user.id,
          body: input.body.trim(),
          mentioned_user_ids: input.mentioned_user_ids,
          parent_comment_id: input.parent_comment_id ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;

      for (const file of files) {
        const path = `${input.freelancer_id}/${crypto.randomUUID()}-${safeFileName(file.name)}`;
        const { error: upErr } = await supabase.storage.from(FREELANCER_FILES_BUCKET).upload(path, file, { contentType: file.type || undefined });
        if (upErr) throw new Error(`Kommentaren er lagret, men ${file.name} kunne ikke lastes opp: ${upErr.message}`);
        const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : null;
        const { error: attErr } = await supabase.from("freelancer_comment_attachments").insert({
          comment_id: data.id,
          freelancer_id: input.freelancer_id,
          storage_path: path,
          name: file.name,
          file_extension: ext,
          file_size: file.size,
          uploaded_by: user.id,
        });
        if (attErr) throw attErr;
      }
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_comments", { freelancerId: vars.freelancer_id });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteFreelancerComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; freelancerId: string }) => {
      const { error } = await supabase.from("freelancer_comments").update({ deleted_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_comments", { freelancerId: vars.freelancerId });
      toast.success("Kommentaren er slettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette kommentaren: ${err.message}`),
  });
}

/** Tidsbegrenset lenke (10 min) til en fil i den private bucketen. */
export async function openFreelancerFile(storagePath: string): Promise<void> {
  const { data, error } = await supabase.storage.from(FREELANCER_FILES_BUCKET).createSignedUrl(storagePath, 600);
  if (error || !data?.signedUrl) {
    toast.error("Kunne ikke åpne filen");
    return;
  }
  window.open(data.signedUrl, "_blank", "noopener,noreferrer");
}

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

/** VIB-2934: vedlegg som kan vises som miniatyr i loggen. */
export function isImageAttachment(a: Pick<DBFreelancerAttachment, "file_extension" | "storage_path">): boolean {
  return !!a.storage_path && IMAGE_EXTENSIONS.has((a.file_extension ?? "").replace(/^\./, "").toLowerCase());
}

/**
 * VIB-2934: tidsbegrensede lenker (10 min) til bildevedlegg, hentet samlet for
 * miniatyrene i loggen. Hentes på nytt etter 8 minutter, før lenkene utløper.
 */
export function useFreelancerImageUrls(freelancerId: string | undefined, paths: string[]) {
  return useQuery({
    queryKey: ["freelancer-image-urls", freelancerId, paths],
    enabled: !!freelancerId && paths.length > 0,
    staleTime: 8 * 60 * 1000,
    refetchInterval: 8 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase.storage.from(FREELANCER_FILES_BUCKET).createSignedUrls(paths, 600);
      if (error) throw error;
      return new Map((data ?? []).filter((d) => d.signedUrl && d.path).map((d) => [d.path as string, d.signedUrl]));
    },
  });
}
